import { Fragment, type ReactNode } from 'react';
import type { DashboardDocument, DashboardElement, SectionDto } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { fileUrl as defaultFileUrl } from '../file-url';
import { DEFAULT_TIME_ZONE } from '../live/format';
import { ElementView } from './ElementView';
import { SceneBackground } from './SceneBackground';
import { SceneProvider, type RenderMode, type RenderVariant } from './scene';

export interface DashboardSceneProps {
  document: DashboardDocument;
  /** sections in card order (section grid, marker links); hidden ones are skipped */
  sections?: readonly SectionDto[];
  variant?: RenderVariant;
  /** default: `view` for the viewer variant, `edit` for the canvas variant */
  mode?: RenderMode;
  timeZone?: string;
  /** logo of the viewer title block (first title element) */
  brandLogoUrl?: string | null;
  fileUrl?: (fileId: string) => string;
  onOpenSection?: (sectionId: string) => void;
  /** render hidden elements at 50 % (editor) instead of skipping them */
  showHidden?: boolean;
  /** wrap each element (editor selection / hit-testing). `node` is the positioned `ElementView`. */
  renderElement?: (element: DashboardElement, node: ReactNode, index: number) => ReactNode;
  className?: string;
  /** overlays rendered above all elements, inside the stage */
  children?: ReactNode;
}

/**
 * The whole dashboard document inside a `<Stage>`: background, then elements in array order (index 0 = back).
 * The same renderer serves the viewer (`variant="viewer"`) and the editor canvas (`variant="canvas"`).
 */
export function DashboardScene({
  document,
  sections = [],
  variant = 'viewer',
  mode,
  timeZone = DEFAULT_TIME_ZONE,
  brandLogoUrl = null,
  fileUrl = defaultFileUrl,
  onOpenSection,
  showHidden = false,
  renderElement,
  className,
  children,
}: DashboardSceneProps) {
  const firstTitle = document.elements.findIndex((e) => e.type === 'title' && (showHidden || !e.hidden));
  const markers = document.elements.flatMap((e) => (e.type === 'marker' && (showHidden || !e.hidden) ? [{ id: e.id, x: e.x, y: e.y, scale: e.scale }] : []));
  return (
    <SceneProvider
      value={{ variant, mode: mode ?? (variant === 'viewer' ? 'view' : 'edit'), sections, timeZone, fileUrl, brandLogoUrl, onOpenSection, markers }}
    >
      <div data-dashboard-scene="" className={cn('absolute inset-0 overflow-hidden', variant === 'viewer' ? 'font-display' : 'font-sans', className)}>
        <SceneBackground background={document.background} />
        {document.elements.map((el, index) => {
          if (el.hidden && !showHidden) return null;
          // an explicit `showLogo` on a title wins; otherwise the first visible title carries the logo (14:7285)
          const view = <ElementView element={el} showLogo={el.type === 'title' && el.showLogo !== undefined ? el.showLogo : index === firstTitle} />;
          const node = el.hidden ? <div className="opacity-50">{view}</div> : view;
          return <Fragment key={el.id}>{renderElement ? renderElement(el, node, index) : node}</Fragment>;
        })}
        {children}
      </div>
    </SceneProvider>
  );
}
